import axios from 'axios';
import { executeCommand } from '../Utils/SendCommandToACC';
import { isMockMode } from '../Knowledge/mockMode';
import { getPreferredGymId } from '../Tasks/TaskUtils';
import { commandError, isNetworkError, runWith } from '../Utils/accClient';
import { LESSON_KIND_LABELS } from '../Knowledge/lessonBlock';
import { mockLearningExecuteCommand } from './mockLearningData';

/**
 * Training (LMS) API client. One function per Learning command in the
 * cross-repo contract (docs/LMS_CONTRACT.md section 6), served by the ACC
 * Lambda or, in mock mode, by `mockLearningData`.
 *
 * `run` is the Utils/accClient contract: every thrown error carries `code`
 * (server errorCode or null), `details` and `isNetwork`. Pages branch on
 * `code` (`E_LESSON_LOCKED` renders the locked screen, `E_CHECK_INCOMPLETE`
 * names the missing questions) and never on message text.
 */

const dispatch = (command, args = []) =>
    (isMockMode() ? mockLearningExecuteCommand(command, args) : executeCommand(command, args));

export const run = runWith(dispatch);

// --- Learner commands (all roles) ---
export const getMyTraining = () => run('get_my_training', []);
export const getCourses = () => run('get_courses', []);
export const getCourse = (courseId) => run('get_course', [courseId]);
export const startLesson = (articleId) => run('start_lesson', [articleId]);
/** `answers` is `{ questionId: choiceIndex }`; the server grades and keeps the attempt. */
export const submitKnowledgeCheck = (articleId, answers) => run('submit_knowledge_check', [articleId, answers]);
export const getMyCertifications = () => run('get_my_certifications', []);
export const getCertificateUploadUrl = (kind, contentType) => run('get_certificate_upload_url', [kind, contentType]);
export const recordCertification = (kind, record) => run('record_certification', [kind, record]);

/** Presigned PUT of the certificate bytes; the mock has no bucket, so it is a no-op there. */
export const uploadCertificate = async (uploadUrl, file, contentType) => {
    if (isMockMode()) return;
    try {
        await axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } });
    } catch (error) {
        throw commandError('Certificate did not upload. Try again.', { isNetwork: isNetworkError(error), cause: error });
    }
};

// --- Training admin commands (Manager, Admin) ---
export const getTrainingMatrix = (gymId = getPreferredGymId()) => run('get_training_matrix', [gymId]);
export const getTrainerTraining = (trainer) => run('get_trainer_training', [trainer]);
export const passLessonForTrainer = (trainer, articleId, note = '') => run('pass_lesson_for_trainer', [trainer, articleId, note]);
export const waiveLessonForTrainer = (trainer, articleId, note = '') => run('waive_lesson_for_trainer', [trainer, articleId, note]);
export const resetLessonProgress = (trainer, articleId) => run('reset_lesson_progress', [trainer, articleId]);
export const logShadowShift = (trainer, date, details = {}) => run('log_shadow_shift', [trainer, date, details]);
export const signOffCompetency = (trainer, courseId, details = {}) => run('sign_off_competency', [trainer, courseId, details]);
export const verifyCertification = (trainer, kind) => run('verify_certification', [trainer, kind]);
export const getCertificateViewUrl = (fileKey) => run('get_certificate_view_url', [fileKey]);
export const saveCourse = (payload) => run('save_course', [payload]);
export const deleteCourse = (courseId) => run('delete_course', [courseId]);
export const setTrainingDue = (trainer, courseId, dueAt) => run('set_training_due', [trainer, courseId, dueAt]);
export const exportTrainingMatrix = (gymId = getPreferredGymId()) => run('export_training_matrix', [gymId]);
export const runTrainingReminders = () => run('run_training_reminders', []);

// --- Pure helpers ---

const MODULE_STATE_LABELS = {
    locked: 'Locked',
    available: 'Available',
    in_progress: 'In progress',
    passed: 'Passed',
    expired: 'Refresher due',
    pending_cosign: 'Waiting on manager',
    pending_verify: 'Waiting on verify',
    pending_signoff: 'Ready for sign-off',
    waived: 'Waived',
};

export const moduleStateLabel = (state) => MODULE_STATE_LABELS[state] || '';

export const lessonKindLabel = (kind) => LESSON_KIND_LABELS[kind] || 'Read';

const CERT_STATUS_LABELS = {
    missing: 'Missing',
    unverified: 'Waiting on verify',
    current: 'Current',
    expiring: 'Expiring',
    expired: 'Expired',
};

export const certStatusLabel = (status) => CERT_STATUS_LABELS[status] || '';

const COURSE_STATUS_LABELS = {
    not_started: 'Not started',
    in_progress: 'In progress',
    complete: 'Complete',
    cleared: 'Cleared',
};

export const courseStatusLabel = (status) => COURSE_STATUS_LABELS[status] || '';

/**
 * `YYYY-MM-DD` is a calendar day, not an instant: parsed as local midnight so
 * the printed day never shifts west of UTC. Anything else is an ISO instant.
 */
const toDate = (value) => {
    if (!value) return null;
    const text = String(value);
    const m = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(text);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** `Sep 14`, or '' when unparseable. */
export const shortDate = (value) => {
    const d = toDate(value);
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from today to `expiresAt` (negative when past), null when unset. */
export const daysUntil = (expiresAt, now = new Date()) => {
    const d = toDate(expiresAt);
    if (!d) return null;
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.round((end - start) / DAY_MS);
};

/** `24 days left`, `1 day left`, `Expires today`, `Expired`; '' when there is no expiry. */
export const daysLeftLabel = (expiresAt, now = new Date()) => {
    const days = daysUntil(expiresAt, now);
    if (days === null) return '';
    if (days < 0) return 'Expired';
    if (days === 0) return 'Expires today';
    return `${days} day${days === 1 ? '' : 's'} left`;
};

/** `6 of 10 lessons` from a CourseView `progress`. */
export const progressLabel = (progress) => {
    const passed = Number(progress?.passed) || 0;
    const total = Number(progress?.total) || 0;
    return `${passed} of ${total} lesson${total === 1 ? '' : 's'}`;
};

/** The streak-board tile value: `Cleared`, `Start`, or `Course 6/10`. */
export const trainingTileValue = (course) => {
    if (!course) return '';
    if (course.status === 'cleared') return 'Cleared';
    if (course.status === 'not_started') return 'Start';
    return `Course ${Number(course.progress?.passed) || 0}/${Number(course.progress?.total) || 0}`;
};

/** `Unlocks after Booth Overview (Hardware).` */
export const lockedLabel = (lockedBy) => {
    const names = (Array.isArray(lockedBy) ? lockedBy : []).filter(Boolean);
    if (!names.length) return 'Unlocks after an earlier module.';
    if (names.length === 1) return `Unlocks after ${names[0]}.`;
    return `Unlocks after ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}.`;
};

/**
 * Questions right to pass: the smallest count whose exact quotient reaches
 * `passScore`, the way the server grades. The product is rounded to six
 * places first so 0.56 * 25 reads as 14, not the 14.000000000000002 that
 * `ceil` alone would turn into 15.
 */
export const passCount = (passScore, total) => Math.ceil(Math.round((Number(passScore) || 0) * (Number(total) || 0) * 1e6) / 1e6);

/** `Passed. 9 of 9.` or `7 of 9. Pass is 8. Review and try again.` */
export const scoreLine = (result) => {
    if (!result) return '';
    const { correct = 0, total = 0, passed, passScore = 0 } = result;
    if (passed) return `Passed. ${correct} of ${total}.`;
    return `${correct} of ${total}. Pass is ${passCount(passScore, total)}. Review and try again.`;
};

/** `Walk finished Sep 14. Waiting on a manager.` */
export const pendingCosignLabel = (flowFinishedAt) => {
    const day = shortDate(flowFinishedAt);
    return `${day ? `Walk finished ${day}. ` : ''}Waiting on a manager.`;
};

/** `/shift-flow/close` or `/shift-flow/sop/<articleId>`; null when the lesson has no linked flow. */
export const flowPathFor = (flowKind) => {
    const text = String(flowKind || '').trim();
    if (!text) return null;
    if (text.startsWith('sop:')) {
        const articleId = text.slice(4).trim();
        return articleId ? `/shift-flow/sop/${encodeURIComponent(articleId)}` : null;
    }
    return `/shift-flow/${encodeURIComponent(text)}`;
};

const ID_RE = /^[A-Za-z0-9_-]+$/;
const YOUTUBE_HOSTS = new Set(['www.youtube.com', 'youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com']);

/**
 * The embed URL for an allow-listed media link, or null for anything else.
 * Only a non-null result may become an iframe; the rest is a plain link.
 */
export const embedUrlFor = (media) => {
    const raw = typeof media === 'string' ? media : media?.url;
    if (!raw) return null;
    let url;
    try {
        url = new URL(String(raw).trim());
    } catch (e) {
        return null;
    }
    if (url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase();
    const segments = url.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be') {
        const id = segments[0];
        return id && ID_RE.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (YOUTUBE_HOSTS.has(host)) {
        let id = url.searchParams.get('v');
        if (!id && (segments[0] === 'embed' || segments[0] === 'shorts' || segments[0] === 'live')) id = segments[1];
        return id && ID_RE.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (host === 'www.loom.com' || host === 'loom.com') {
        if (segments[0] !== 'share' && segments[0] !== 'embed') return null;
        const id = segments[1];
        return id && ID_RE.test(id) ? `https://www.loom.com/embed/${id}` : null;
    }
    if (host === 'vimeo.com') {
        const id = segments[0];
        return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
    }
    if (host === 'player.vimeo.com') {
        const id = segments[0] === 'video' ? segments[1] : null;
        return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null;
};

/** The course, module and lesson row for one article inside a `get_my_training` payload, or null. */
export const findLesson = (training, articleId) => {
    for (const course of training?.courses || []) {
        for (const module of course.modules || []) {
            const lesson = (module.lessons || []).find((l) => l.articleId === articleId);
            if (lesson) return { course, module, lesson };
        }
    }
    return null;
};

/** The Certification entry for a kind, or null. */
export const findCertification = (training, kind) =>
    (training?.certifications || []).find((c) => c.kind === kind) || null;

// --- Certificates ---

/** Mirror of `Constants.CERT_KINDS[kind].validDays`; null means the certificate never expires. */
export const CERT_VALID_DAYS = { cpr_aed: 730, harassment: null };

export const CERT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const CERT_MAX_BYTES = 10 * 1024 * 1024;

/** Type and size gate for a certificate file, checked before any upload call. */
export const isAcceptableCertificateFile = (file) =>
    Boolean(file) && CERT_CONTENT_TYPES.includes(file.type) && Number(file.size) > 0 && Number(file.size) <= CERT_MAX_BYTES;

/**
 * `completedAt + validDays` as YYYY-MM-DD, '' when the kind has no expiry or
 * the date is unusable. UTC arithmetic so a DST change never shifts the day.
 */
export const defaultCertExpiry = (kind, completedAt) => {
    const days = CERT_VALID_DAYS[kind];
    const m = String(completedAt || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!days || !m) return '';
    const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + days * DAY_MS;
    return new Date(t).toISOString().slice(0, 10);
};

const pad2 = (n) => String(n).padStart(2, '0');

/** The local calendar day as YYYY-MM-DD, the same frame a date input reports. */
export const localYmd = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** `640 KB`, `1.2 MB`. */
export const fileSizeLabel = (bytes) => {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
};

/** `Expires in 24 days.`, `Expires in 1 day.`, `Expires today.`; '' when there is no expiry or it has passed. */
export const expiresInLabel = (expiresAt, now = new Date()) => {
    const days = daysUntil(expiresAt, now);
    if (days === null || days < 0) return '';
    if (days === 0) return 'Expires today.';
    return `Expires in ${days} day${days === 1 ? '' : 's'}.`;
};

/**
 * `Passed Sep 14 (co-signed by Jordan).` from a lesson row. `passedBy` is
 * `manager:<name>`, `flow:<runId>`, `quiz`, `cert` or `signoff`; only the
 * first two say something a trainer cares about.
 */
export const passedLine = (passedAt, passedBy) => {
    const day = shortDate(passedAt);
    const by = String(passedBy || '');
    let how = '';
    if (by.startsWith('manager:')) {
        const name = by.slice(8).trim();
        how = name ? ` (co-signed by ${name})` : ' (co-signed by a manager)';
    } else if (by.startsWith('flow:')) {
        how = ' (from a finished walk)';
    }
    return `Passed${day ? ` ${day}` : ''}${how}.`;
};
